// @tennis-matches/services/winterdoppel/series

import { getSupabaseClient } from "../supabase/index.js";
import {
  assertWinterDoppelPlanEditable,
  canReopenWinterDoppelSeriesPlan,
  hasWinterDoppelStructuralUpdates,
  isWinterDoppelPlanFinal,
} from "./planLock.js";
import {
  WINTERDOPPEL_SERIES_KIND_DEFAULT,
  WINTERDOPPEL_SERIES_KINDS,
  WINTERDOPPEL_SERIES_MODE_DEFAULT,
  WINTERDOPPEL_SERIES_MODES,
  WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
  WINTERDOPPEL_FAIRNESS_MODES,
} from "./constants.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __asWeekdays(v) {
  const arr = Array.isArray(v) ? v : [];
  return [
    ...new Set(arr.map((n) => Number(n)).filter((n) => n >= 1 && n <= 7)),
  ];
}

function __asSeriesMode(v) {
  const mode = __asStr(v) || WINTERDOPPEL_SERIES_MODE_DEFAULT;
  return WINTERDOPPEL_SERIES_MODES.includes(mode)
    ? mode
    : WINTERDOPPEL_SERIES_MODE_DEFAULT;
}

function __asFairnessMode(v) {
  const mode = __asStr(v) || WINTERDOPPEL_FAIRNESS_MODE_DEFAULT;
  return WINTERDOPPEL_FAIRNESS_MODES.includes(mode)
    ? mode
    : WINTERDOPPEL_FAIRNESS_MODE_DEFAULT;
}

const SERIES_SELECT =
  "id,tenant_id,created_by,location_id,name,notes,weekdays,start_time,end_time,court_count,season_start,season_end,holiday_subdivision,series_kind,series_mode,fairness_mode,notify_cancel_enabled,notify_cancel_emails,invite_token,schedule_token,schedule_password_enabled,join_password_enabled,is_active,plan_status,finalized_at,finalized_by,draft_autofill_at,created_at,updated_at";

/** @param {unknown} raw */
export function normalizeWinterDoppelNotifyCancelEmails(raw) {
  const parts = Array.isArray(raw)
    ? raw
    : String(raw ?? "")
        .split(/[\n,;]+/)
        .map((s) => s.trim());
  const out = [];
  const seen = new Set();
  for (const part of parts) {
    const email = String(part || "")
      .trim()
      .toLowerCase();
    if (!email || !email.includes("@") || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

export async function loadWinterDoppelSeries({
  tenantId,
  activeOnly = true,
} = {}) {
  const supabase = await getSupabaseClient();
  let q = supabase
    .from("winterdoppel_series")
    .select(SERIES_SELECT)
    .order("season_start", { ascending: false })
    .order("name", { ascending: true });
  if (tenantId) q = q.eq("tenant_id", __asStr(tenantId));
  if (activeOnly) q = q.eq("is_active", true);
  const { data, error } = await q;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function getWinterDoppelSeries(id) {
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_series")
    .select(SERIES_SELECT)
    .eq("id", __asStr(id))
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function createWinterDoppelSeries(input = {}) {
  const supabase = await getSupabaseClient();
  const row = {
    name: __asStr(input.name),
    notes: __asStr(input.notes) || null,
    location_id: __asStr(input.location_id || input.locationId) || null,
    weekdays: __asWeekdays(input.weekdays),
    start_time:
      __asStr(input.start_time || input.startTime || "18:00") || "18:00",
    end_time: __asStr(input.end_time || input.endTime) || null,
    court_count: Math.min(
      8,
      Math.max(1, Number(input.court_count ?? input.courtCount) || 1),
    ),
    season_start: __asStr(input.season_start || input.seasonStart),
    season_end: __asStr(input.season_end || input.seasonEnd),
    holiday_subdivision:
      __asStr(input.holiday_subdivision || input.holidaySubdivision) || null,
    series_kind: (() => {
      const kind =
        __asStr(input.series_kind || input.seriesKind) ||
        WINTERDOPPEL_SERIES_KIND_DEFAULT;
      return WINTERDOPPEL_SERIES_KINDS.includes(kind)
        ? kind
        : WINTERDOPPEL_SERIES_KIND_DEFAULT;
    })(),
    series_mode: __asSeriesMode(input.series_mode || input.seriesMode),
    fairness_mode: __asFairnessMode(input.fairness_mode || input.fairnessMode),
    notify_cancel_enabled: Boolean(
      input.notify_cancel_enabled ?? input.notifyCancelEnabled,
    ),
    notify_cancel_emails: normalizeWinterDoppelNotifyCancelEmails(
      input.notify_cancel_emails ?? input.notifyCancelEmails,
    ),
    is_active: input.is_active !== false && input.isActive !== false,
  };
  const tid = __asStr(input.tenant_id || input.tenantId);
  if (tid) row.tenant_id = tid;
  if (!row.name || !row.season_start || !row.season_end) {
    throw new Error("winterdoppel.series.missingFields");
  }
  if (!row.weekdays.length) {
    throw new Error("winterdoppel.series.missingWeekdays");
  }
  const { data, error } = await supabase
    .from("winterdoppel_series")
    .insert(row)
    .select(SERIES_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function updateWinterDoppelSeries(id, updates = {}) {
  const current = await getWinterDoppelSeries(id);
  if (!current) throw new Error("winterdoppel.series.notFound");
  if (
    isWinterDoppelPlanFinal(current) &&
    hasWinterDoppelStructuralUpdates(updates)
  ) {
    assertWinterDoppelPlanEditable(current);
  }

  const supabase = await getSupabaseClient();
  const patch = {};
  if (updates.name != null) patch.name = __asStr(updates.name);
  if (updates.notes != null) patch.notes = __asStr(updates.notes) || null;
  if (updates.location_id != null || updates.locationId != null) {
    patch.location_id =
      __asStr(updates.location_id ?? updates.locationId) || null;
  }
  if (updates.weekdays != null) patch.weekdays = __asWeekdays(updates.weekdays);
  if (updates.start_time != null || updates.startTime != null) {
    patch.start_time =
      __asStr(updates.start_time ?? updates.startTime) || "18:00";
  }
  if (updates.end_time != null || updates.endTime != null) {
    patch.end_time = __asStr(updates.end_time ?? updates.endTime) || null;
  }
  if (updates.court_count != null || updates.courtCount != null) {
    patch.court_count = Math.min(
      8,
      Math.max(1, Number(updates.court_count ?? updates.courtCount) || 1),
    );
  }
  if (updates.season_start != null || updates.seasonStart != null) {
    patch.season_start = __asStr(updates.season_start ?? updates.seasonStart);
  }
  if (updates.season_end != null || updates.seasonEnd != null) {
    patch.season_end = __asStr(updates.season_end ?? updates.seasonEnd);
  }
  if (
    updates.holiday_subdivision != null ||
    updates.holidaySubdivision != null
  ) {
    patch.holiday_subdivision =
      __asStr(updates.holiday_subdivision ?? updates.holidaySubdivision) ||
      null;
  }
  if (updates.series_kind != null || updates.seriesKind != null) {
    const kind =
      __asStr(updates.series_kind ?? updates.seriesKind) ||
      WINTERDOPPEL_SERIES_KIND_DEFAULT;
    patch.series_kind = WINTERDOPPEL_SERIES_KINDS.includes(kind)
      ? kind
      : WINTERDOPPEL_SERIES_KIND_DEFAULT;
  }
  if (updates.series_mode != null || updates.seriesMode != null) {
    patch.series_mode = __asSeriesMode(
      updates.series_mode ?? updates.seriesMode,
    );
  }
  if (updates.fairness_mode != null || updates.fairnessMode != null) {
    patch.fairness_mode = __asFairnessMode(
      updates.fairness_mode ?? updates.fairnessMode,
    );
  }
  if (
    updates.notify_cancel_enabled != null ||
    updates.notifyCancelEnabled != null
  ) {
    patch.notify_cancel_enabled = Boolean(
      updates.notify_cancel_enabled ?? updates.notifyCancelEnabled,
    );
  }
  if (
    updates.notify_cancel_emails != null ||
    updates.notifyCancelEmails != null
  ) {
    patch.notify_cancel_emails = normalizeWinterDoppelNotifyCancelEmails(
      updates.notify_cancel_emails ?? updates.notifyCancelEmails,
    );
  }
  if (updates.is_active != null || updates.isActive != null) {
    patch.is_active = updates.is_active ?? updates.isActive;
  }
  const { data, error } = await supabase
    .from("winterdoppel_series")
    .update(patch)
    .eq("id", __asStr(id))
    .select(SERIES_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function finalizeWinterDoppelSeries(id) {
  const current = await getWinterDoppelSeries(id);
  if (!current) throw new Error("winterdoppel.series.notFound");
  if (isWinterDoppelPlanFinal(current)) {
    return current;
  }
  if (String(current.plan_status || "draft") !== "draft") {
    throw new Error("winterdoppel.plan.invalidTransition");
  }

  const supabase = await getSupabaseClient();
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id || null;
  const { data, error } = await supabase
    .from("winterdoppel_series")
    .update({
      plan_status: "final",
      finalized_at: new Date().toISOString(),
      finalized_by: uid,
    })
    .eq("id", __asStr(id))
    .eq("plan_status", "draft")
    .select(SERIES_SELECT)
    .single();
  if (error) throw error;

  // Bestätigter Plan: Starter sind standardmäßig dabei (nicht „noch offen“).
  const { data: sessions, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select("id")
    .eq("series_id", __asStr(id));
  if (sessErr) throw sessErr;
  const sessionIds = (sessions || []).map((s) => s.id).filter(Boolean);
  if (sessionIds.length) {
    const { error: rsvpErr } = await supabase
      .from("winterdoppel_session_players")
      .update({ rsvp: "yes" })
      .in("session_id", sessionIds)
      .eq("role", "starter")
      .eq("rsvp", "pending");
    if (rsvpErr) throw rsvpErr;
  }

  return data;
}

/** Finalisierten Plan wieder öffnen (draft), nur vor dem ersten Termin. */
export async function reopenWinterDoppelSeriesPlan(id, opts = {}) {
  const current = await getWinterDoppelSeries(id);
  if (!current) throw new Error("winterdoppel.series.notFound");
  if (!isWinterDoppelPlanFinal(current)) return current;

  let sessions = opts.sessions;
  if (sessions == null) {
    const supabase = await getSupabaseClient();
    const { data: sessionRows, error: sessErr } = await supabase
      .from("winterdoppel_sessions")
      .select("id,session_date,status")
      .eq("series_id", __asStr(id));
    if (sessErr) throw sessErr;
    sessions = sessionRows || [];
  }

  const gate = canReopenWinterDoppelSeriesPlan({
    series: current,
    sessions,
    today: opts.today,
  });
  if (!gate.ok) {
    throw new Error(
      gate.reason === "seasonStarted"
        ? "winterdoppel.plan.reopenTooLate"
        : "winterdoppel.plan.reopenDenied",
    );
  }

  const supabase = await getSupabaseClient();
  const { data, error } = await supabase.rpc(
    "winterdoppel_reopen_series_plan",
    {
      p_series_id: __asStr(id),
    },
  );
  if (error) throw error;
  return data || (await getWinterDoppelSeries(id));
}

export async function archiveWinterDoppelSeries(id) {
  const current = await getWinterDoppelSeries(id);
  if (!current) throw new Error("winterdoppel.series.notFound");
  if (!current.is_active) return current;
  return updateWinterDoppelSeries(id, { is_active: false });
}

async function __countPlayersForSeries(seriesId) {
  const supabase = await getSupabaseClient();
  const { count, error } = await supabase
    .from("winterdoppel_players")
    .select("id", { count: "exact", head: true })
    .eq("series_id", __asStr(seriesId));
  if (error) throw error;
  return Number(count) || 0;
}

export async function deleteWinterDoppelSeries(id) {
  const current = await getWinterDoppelSeries(id);
  if (!current) throw new Error("winterdoppel.series.notFound");
  const playerCount = await __countPlayersForSeries(id);
  if (playerCount > 0) {
    throw new Error("winterdoppel.series.deleteHasPlayers");
  }
  const supabase = await getSupabaseClient();
  const { error } = await supabase
    .from("winterdoppel_series")
    .delete()
    .eq("id", __asStr(id));
  if (error) throw error;
  return true;
}
